import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';

export type UiFlowNodeFrameShape =
  | 'rect'
  | 'pill'
  | 'diamond'
  | 'circle'
  | 'parallelogram'
  | 'document'
  | 'pentagon';
export type UiFlowNodeFrameTone = 'default' | 'muted' | 'primary';

const DIAMOND_SIZE_PX = 132;
const DIAMOND_FONT_MAX_PX = 13; // ~0.8125rem
const DIAMOND_FONT_MIN_PX = 10.4; // ~0.65rem
const DIAMOND_FONT_STEP_PX = 0.5;
/** Ancho fijo del área tipográfica (px). Independiente del grid del diagrama. */
const DIAMOND_TEXT_WIDTH_PX = 72;

@Component({
  selector: 'ui-flow-node-frame',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ui-flow-node-frame" [class]="frameClass()">
      @if (svgPath()) {
        <svg
          class="ui-flow-node-frame__svg"
          [attr.viewBox]="viewBox()"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <path
            [attr.d]="svgPath()"
            [attr.stroke-width]="svgStrokeWidth()"
          />
        </svg>
      }
      <div #inner class="ui-flow-node-frame__inner">
        <ng-content />
      </div>
    </div>
  `,
  styles: `
    :host { display: inline-flex; max-width: 100%; }
    .ui-flow-node-frame {
      --ui-flow-node-frame-border: color-mix(
        in srgb,
        #e4e4e6 80%,
        #000 20%
      );
      position: relative;
      display: inline-flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: 1.5px solid var(--ui-flow-node-frame-border);
      background: #fff;
      color: #1e293b;
      font-family: var(--font-sans, inherit);
    }
    .ui-flow-node-frame__inner {
      position: relative;
      z-index: 1;
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    .ui-flow-node-frame--rect {
      min-width: 112px;
      min-height: 44px;
      padding: 8px 12px;
      border-radius: 10px;
    }
    .ui-flow-node-frame--pill {
      --ui-flow-node-frame-border: var(--color-primary, #4169e2);
      min-width: var(--ui-flow-node-frame-pill-min-width, 128px);
      min-height: 44px;
      padding: 8px var(--ui-flow-node-frame-pill-padding-inline, 18px);
      border-radius: 999px;
      border-color: var(--ui-flow-node-frame-border);
    }
    .ui-flow-node-frame--circle {
      width: 76px;
      height: 76px;
      padding: 8px;
      border-radius: 50%;
    }
    .ui-flow-node-frame--parallelogram {
      min-width: 132px;
      min-height: 72px;
      padding: 10px 20px;
      border-radius: 4px;
      transform: skewX(-18deg);
    }
    .ui-flow-node-frame--parallelogram .ui-flow-node-frame__inner {
      transform: skewX(18deg);
    }
    .ui-flow-node-frame--diamond {
      width: ${DIAMOND_SIZE_PX}px;
      max-width: ${DIAMOND_SIZE_PX}px;
      height: ${DIAMOND_SIZE_PX}px;
      flex: 0 0 ${DIAMOND_SIZE_PX}px;
      padding: 0;
      border: 0;
      background: transparent;
    }
    .ui-flow-node-frame--diamond .ui-flow-node-frame__svg path {
      stroke-dasharray: 4 3;
    }
    /* Caja tipográfica fija en px: igual en átomo, molécula y diagrama. */
    .ui-flow-node-frame--diamond .ui-flow-node-frame__inner {
      position: absolute;
      inset: auto;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      box-sizing: border-box;
      width: ${DIAMOND_TEXT_WIDTH_PX}px;
      max-width: ${DIAMOND_TEXT_WIDTH_PX}px;
      min-width: 0;
      height: auto;
      padding: 0;
      text-align: center;
      word-break: normal;
      overflow-wrap: normal;
      hyphens: none;
      white-space: normal;
    }
    :host ::ng-deep .ui-flow-node-frame--diamond ui-flow-label,
    :host ::ng-deep .ui-flow-node-frame--diamond .ui-flow-label {
      display: block;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      text-align: center;
      white-space: normal;
      overflow-wrap: normal;
      word-break: normal;
    }
    .ui-flow-node-frame--document,
    .ui-flow-node-frame--pentagon {
      padding: 0;
      border: 0;
      background: transparent;
    }
    .ui-flow-node-frame--document {
      width: 132px;
      height: 96px;
    }
    .ui-flow-node-frame--pentagon {
      width: 120px;
      height: 116px;
    }
    .ui-flow-node-frame__svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .ui-flow-node-frame__svg path {
      fill: #fff;
      stroke: var(--ui-flow-node-frame-border);
      stroke-linejoin: round;
      vector-effect: non-scaling-stroke;
    }
    .ui-flow-node-frame--muted {
      --ui-flow-node-frame-border: color-mix(in srgb, #cbd5e1 80%, #000 20%);
      background: #f1f5f9;
      border-color: var(--ui-flow-node-frame-border);
      color: #334155;
    }
    .ui-flow-node-frame--muted .ui-flow-node-frame__svg path {
      fill: #f1f5f9;
      stroke: var(--ui-flow-node-frame-border);
    }
    .ui-flow-node-frame--primary {
      background: var(--color-primary, #4169e2);
      border-color: var(--color-primary, #4169e2);
      color: #fff;
    }
    .ui-flow-node-frame--primary .ui-flow-node-frame__svg path {
      fill: var(--color-primary, #4169e2);
      stroke: var(--color-primary, #4169e2);
    }
    :host ::ng-deep .ui-flow-node-frame--primary .ui-flow-label,
    :host ::ng-deep .ui-flow-node-frame--primary .ui-flow-label--strong,
    :host ::ng-deep .ui-flow-node-frame--primary .ui-flow-label--muted,
    :host ::ng-deep .ui-flow-node-frame--primary .ui-flow-label--default {
      color: #fff;
    }
    .ui-flow-node-frame--document .ui-flow-node-frame__inner,
    .ui-flow-node-frame--pentagon .ui-flow-node-frame__inner {
      width: auto;
      max-width: 82%;
      padding: 10px;
    }
    .ui-flow-node-frame--pentagon .ui-flow-node-frame__inner {
      margin-bottom: 22px;
    }
  `,
})
export class UiFlowNodeFrameComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inner = viewChild.required<ElementRef<HTMLElement>>('inner');
  private lastFitKey = '';
  private resizeObserver: ResizeObserver | null = null;

  readonly shape = input<UiFlowNodeFrameShape>('rect');
  readonly tone = input<UiFlowNodeFrameTone>('default');

  protected readonly frameClass = computed(
    () => `ui-flow-node-frame--${this.shape()} ui-flow-node-frame--${this.tone()}`,
  );

  protected readonly viewBox = computed(() => {
    switch (this.shape()) {
      case 'diamond':
        return `0 0 ${DIAMOND_SIZE_PX} ${DIAMOND_SIZE_PX}`;
      case 'document':
        return '0 0 132 96';
      case 'pentagon':
        return '0 0 120 116';
      default:
        return null;
    }
  });

  protected readonly svgPath = computed(() => {
    switch (this.shape()) {
      case 'diamond':
        return 'M66 3 L129 66 L66 129 L3 66 Z';
      case 'document':
        return 'M3 3 H129 V70 C112 84 100 62 82 71 C64 80 52 61 34 71 C22 77 12 75 3 71 Z';
      case 'pentagon':
        return 'M3 3 H117 V64 L60 113 L3 64 Z';
      default:
        return null;
    }
  });

  /**
   * Con vector-effect:non-scaling-stroke el valor es en px de pantalla.
   * Doc/Ref van más finos para igualar el border CSS del circle.
   */
  protected readonly svgStrokeWidth = computed(() => {
    const shape = this.shape();
    if (shape === 'document' || shape === 'pentagon') return '0.75px';
    return '1.5px';
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    });

    afterRenderEffect(() => {
      const shape = this.shape();
      const innerEl = this.inner().nativeElement;
      const hostEl = this.host.nativeElement;

      if (shape !== 'diamond') {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        if (this.lastFitKey !== '') {
          hostEl.style.removeProperty('--ui-flow-label-font-size');
          this.lastFitKey = '';
        }
        return;
      }

      this.fitDiamondLabel(hostEl, innerEl);
      this.ensureResizeObserver(hostEl, innerEl);
    });
  }

  private ensureResizeObserver(hostEl: HTMLElement, innerEl: HTMLElement): void {
    if (this.resizeObserver) return;
    this.resizeObserver = new ResizeObserver(() => {
      this.fitDiamondLabel(hostEl, innerEl);
    });
    this.resizeObserver.observe(hostEl);
    void document.fonts?.ready?.then(() => {
      this.fitDiamondLabel(hostEl, innerEl);
    });
  }

  /**
   * Baja el font-size solo si alguna palabra no cabe en el ancho fijo del diamante.
   * El wrap entre palabras lo garantiza el max-width de 72px (CSS), no este cálculo.
   */
  private fitDiamondLabel(hostEl: HTMLElement, innerEl: HTMLElement): void {
    const available = DIAMOND_TEXT_WIDTH_PX;
    const text = innerEl.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    if (!text) return;

    const words = text.split(' ').filter(Boolean);
    if (!words.length) return;

    const labelEl = innerEl.querySelector('.ui-flow-label') as HTMLElement | null;
    const styles = getComputedStyle(labelEl ?? innerEl);

    const probe = document.createElement('span');
    probe.style.cssText =
      'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;pointer-events:none;';
    probe.style.fontFamily = styles.fontFamily;
    probe.style.fontWeight = styles.fontWeight;
    probe.style.fontStyle = styles.fontStyle;
    probe.style.letterSpacing = styles.letterSpacing;
    hostEl.appendChild(probe);

    const longestFits = (px: number): boolean => {
      probe.style.fontSize = `${px}px`;
      return words.every((word) => {
        probe.textContent = word;
        return probe.offsetWidth <= available;
      });
    };

    let size = DIAMOND_FONT_MAX_PX;
    while (size > DIAMOND_FONT_MIN_PX && !longestFits(size)) {
      size -= DIAMOND_FONT_STEP_PX;
    }
    if (size < DIAMOND_FONT_MIN_PX) size = DIAMOND_FONT_MIN_PX;

    probe.remove();

    const value = `${size}px`;
    const key = `${text}|${available}|${value}|${styles.fontFamily}|${styles.fontWeight}`;
    if (this.lastFitKey === key) return;
    this.lastFitKey = key;
    hostEl.style.setProperty('--ui-flow-label-font-size', value);
  }
}
